// =====================================================
// FORMULARIO DE MOVIMIENTO GANADERO
// src/pages/Agro/StockGanadero/FormMovimientoGanadero.js
// =====================================================

import React, { useState, useEffect, useRef, useMemo } from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
  FormFeedback,
} from "reactstrap"
import Flatpickr from "react-flatpickr"
import { Spanish } from "flatpickr/dist/l10n/es"
import dayjs from "dayjs"

import {
  getEspeciesGanaderas,
  getCategoriasGanaderas,
  getPrecioHacienda,
  getValoresGanaderia,
  getValoresGenerales,
  saveMovimiento,
  getStockDisponibleEstablecimiento,
} from "api/agro"
import toastHandler from "toastHandler"
import NumberInput from "components/Input/NumberInput"
import AmountInput from "components/Input/AmountInput"

// Constantes para tipos de movimiento
const TIPOS_MOVIMIENTO = [
  { value: "INGRESO", label: "Ingreso", color: "success", icon: "arrow-down" },
  { value: "EGRESO", label: "Egreso", color: "danger", icon: "arrow-up" },
]

// Motivos según tipo de movimiento
const MOTIVOS = {
  INGRESO: [
    { value: "COMPRA", label: "Compra" },
    { value: "NACIMIENTO", label: "Nacimiento" },
    { value: "TRASLADO_INGRESO", label: "Traslado (Ingreso)" },
    { value: "OTRO_INGRESO", label: "Otro" },
  ],
  EGRESO: [
    { value: "VENTA", label: "Venta" },
    { value: "MORTANDAD", label: "Mortandad" },
    { value: "TRASLADO_EGRESO", label: "Traslado (Egreso)" },
    { value: "FAENA", label: "Faena" },
    { value: "OTRO_EGRESO", label: "Otro" },
  ],
}

// Motivos que requieren datos económicos
const MOTIVOS_ECONOMICOS = ["COMPRA", "VENTA"]

// Motivos que requieren RENSPA
const MOTIVOS_TRASLADO = ["TRASLADO_INGRESO", "TRASLADO_EGRESO"]

const FormMovimientoGanadero = ({
  idEstablecimiento,
  establecimientos = [],
  onSuccess,
  onCancel,
}) => {
  const { setError, setSuccess } = toastHandler()
  const [loading, setLoading] = useState(false)
  const [especies, setEspecies] = useState([])
  const [categorias, setCategorias] = useState([])
  const [promedioKgsCabeza, setPromedioKgsCabeza] = useState(null)
  const [valoresGanaderia, setValoresGanaderia] = useState([])
  const [tipoCambioUSD, setTipoCambioUSD] = useState(null)
  const [stockDisponible, setStockDisponible] = useState([])
  const [stockMax, setStockMax] = useState(null)
  // stockMaxRef: mismo valor que stockMax pero accesible sincrónicamente desde el test de Yup
  const stockMaxRef = useRef(null)

  const formik = useFormik({
    initialValues: {
      idEstablecimiento: idEstablecimiento || "",
      fechaMovimiento: new Date(),
      tipoMovimiento: "INGRESO",
      motivo: "COMPRA",
      numeroDte: "",
      idEspecie: "",
      idCategoria: "",
      cantidadCabezas: "",
      cantidadCabezasVenta: "",
      // Datos económicos
      precioKg: "",
      precioCabeza: "",
      pesoTotalKg: "",
      importeTotal: "",
      moneda: "USD",
      // Traslado
      renspaOrigen: "",
      renspaDestino: "",
      establecimientoContraparte: "",
    },
    validationSchema: Yup.object({
      idEstablecimiento: Yup.number().required("Establecimiento requerido"),
      fechaMovimiento: Yup.date()
        .required("Fecha requerida")
        .max(new Date(), "No puede ser futura"),
      tipoMovimiento: Yup.string().required("Tipo requerido"),
      motivo: Yup.string().required("Motivo requerido"),
      idEspecie: Yup.number().required("Especie requerida"),
      cantidadCabezas: Yup.number()
        .min(1, "Mínimo 1 cabeza")
        .test("max-stock-egreso", "", function (value) {
          const max = stockMaxRef.current
          if (max != null && value != null && value > max) {
            return this.createError({
              message: `No puede superar el stock disponible (${max} cabezas)`,
            })
          }
          return true
        })
        .required("Cantidad requerida"),
      cantidadCabezasVenta: Yup.number()
        .min(0, "No puede ser negativo")
        .test("max-cabezas-venta", "", function (value) {
          if (value == null || value === "") return true
          const totalCabezas = this.parent.cantidadCabezas
          if (totalCabezas != null && value > totalCabezas) {
            return this.createError({
              message: `No puede superar las cabezas totales (${totalCabezas})`,
            })
          }
          const max = stockMaxRef.current
          if (max != null && value > max) {
            return this.createError({
              message: `No puede superar el stock disponible para venta (${max} cabezas)`,
            })
          }
          return true
        }),
      // Validaciones opcionales que se evalúan dinámicamente
      precioKg: Yup.number().min(0, "Debe ser positivo"),
      precioCabeza: Yup.number().min(0, "Debe ser positivo"),
      pesoTotalKg: Yup.number().min(0, "Debe ser positivo"),
      moneda: Yup.string().when("motivo", {
        is: motivo => MOTIVOS_ECONOMICOS.includes(motivo),
        then: Yup.string().required("Moneda requerida"),
        otherwise: Yup.string(),
      }),
      renspaOrigen: Yup.string().when(["tipoMovimiento", "motivo"], {
        is: (tipo, motivo) =>
          tipo === "INGRESO" && MOTIVOS_TRASLADO.includes(motivo),
        then: Yup.string().required("RENSPA origen requerido"),
        otherwise: Yup.string(),
      }),
      renspaDestino: Yup.string().when(["tipoMovimiento", "motivo"], {
        is: (tipo, motivo) =>
          tipo === "EGRESO" && MOTIVOS_TRASLADO.includes(motivo),
        then: Yup.string().required("RENSPA destino requerido"),
        otherwise: Yup.string(),
      }),
      establecimientoContraparte: Yup.string().when("motivo", {
        is: motivo => MOTIVOS_TRASLADO.includes(motivo),
        then: Yup.string().required("Establecimiento requerido"),
        otherwise: Yup.string(),
      }),
    }),
    onSubmit: async values => {
      setLoading(true)
      try {
        // Buscar código de especie y categoría
        const especieSeleccionada = especies.find(
          e => e.id === parseInt(values.idEspecie),
        )
        const categoriaSeleccionada = values.idCategoria
          ? categorias.find(c => c.id === parseInt(values.idCategoria))
          : null

        const data = {
          idEstablecimiento: parseInt(values.idEstablecimiento),
          fechaMovimiento: dayjs(values.fechaMovimiento).format("YYYY-MM-DD"),
          tipoMovimiento: values.tipoMovimiento,
          motivo: values.motivo,
          numeroDte: values.numeroDte || null,
          codigoEspecie: especieSeleccionada?.codigo || null,
          codigoCategoria: categoriaSeleccionada?.codigo || null,
          cantidadCabezas: parseInt(values.cantidadCabezas),
          cantidadCabezasVenta: values.cantidadCabezasVenta
            ? parseInt(values.cantidadCabezasVenta)
            : parseInt(values.cantidadCabezas),
        }

        // Agregar datos económicos si corresponde
        if (MOTIVOS_ECONOMICOS.includes(values.motivo)) {
          data.precioKg =
            values.precioKg !== "" ? Number(values.precioKg) : null
          data.precioCabeza =
            values.precioCabeza !== "" ? Number(values.precioCabeza) : null
          data.pesoTotalKg =
            values.pesoTotalKg !== "" ? Number(values.pesoTotalKg) : null
          data.importeTotal =
            values.importeTotal !== "" ? Number(values.importeTotal) : null
          data.moneda = values.moneda
        }

        // Agregar datos de traslado si corresponde
        if (MOTIVOS_TRASLADO.includes(values.motivo)) {
          data.renspaOrigen = values.renspaOrigen || null
          data.renspaDestino = values.renspaDestino || null
          data.establecimientoContraparte =
            values.establecimientoContraparte || null
        }

        await saveMovimiento(data)
        setSuccess("Movimiento registrado correctamente")
        onSuccess && onSuccess()
      } catch (err) {
        const mensaje =
          err?.response?.data?.mensaje || "Error al registrar el movimiento"
        setError(mensaje)
      } finally {
        setLoading(false)
      }
    },
  })

  const { values, errors, touched, handleChange, handleSubmit, setFieldValue } =
    formik

  useEffect(() => {
    getEspeciesGanaderas().then(setEspecies).catch(setError)
  }, [])

  // Cargar stock disponible para el establecimiento seleccionado (usado en EGRESO)
  useEffect(() => {
    const idEst = parseInt(values.idEstablecimiento)
    if (!idEst) {
      setStockDisponible([])
      return
    }
    getStockDisponibleEstablecimiento(idEst)
      .then(data => setStockDisponible(data || []))
      .catch(() => setStockDisponible([]))
  }, [values.idEstablecimiento])

  useEffect(() => {
    Promise.all([getValoresGanaderia(), getValoresGenerales()])
      .then(([valoresData, generalesData]) => {
        setValoresGanaderia(valoresData || [])
        const tc = (generalesData || []).find(
          v => v.codigo === "TIPO_CAMBIO_USD",
        )
        if (tc?.valor) setTipoCambioUSD(parseFloat(tc.valor))
      })
      .catch(() => {})
  }, [])

  // Recargar categorías al cambiar la especie seleccionada
  useEffect(() => {
    const cargarCategorias = async () => {
      if (!values.idEspecie) {
        setCategorias([])
        setFieldValue("idCategoria", "")
        return
      }
      const esp = especies.find(e => e.id === parseInt(values.idEspecie))
      if (!esp || !esp.codigo) {
        setCategorias([])
        setFieldValue("idCategoria", "")
        return
      }
      try {
        const cats = await getCategoriasGanaderas(esp.codigo)
        setCategorias(cats || [])
        setFieldValue("idCategoria", "")
      } catch (err) {
        setError(err)
        setCategorias([])
        setFieldValue("idCategoria", "")
      }
    }
    cargarCategorias()
  }, [values.idEspecie, especies])

  const limpiarPrecios = () => {
    setFieldValue("precioKg", "")
    setFieldValue("precioCabeza", "")
    setFieldValue("pesoTotalKg", "")
    setFieldValue("importeTotal", "")
  }

  // Inferir campos económicos al seleccionar categoría (BDSJEA-187, BDSJEA-199)
  useEffect(() => {
    if (!values.idCategoria) {
      setPromedioKgsCabeza(null)
      limpiarPrecios()
      return
    }
    const cat = categorias.find(c => c.id === parseInt(values.idCategoria))
    if (!cat?.codigo) return

    // Limpiar campos previos antes de cargar los nuevos
    limpiarPrecios()

    // Peso promedio desde valor_ganaderia, match exacto por código de categoría
    const valorRef = valoresGanaderia.find(
      v => v.codigoCategoria === cat.codigo,
    )
    const pesoVenta = valorRef?.pesoVenta || null
    setPromedioKgsCabeza(pesoVenta)
    if (pesoVenta && values.cantidadCabezas !== "") {
      setFieldValue("pesoTotalKg", Number(values.cantidadCabezas) * pesoVenta)
    }

    getPrecioHacienda(cat.codigo)
      .then(precio => {
        if (!precio) return
        // Precio por kg en USD: convertir desde ARS si corresponde
        let precioKgUSD = precio.precioPromedioKg
        if (precio.moneda === "ARS" && tipoCambioUSD) {
          precioKgUSD = precio.precioPromedioKg / tipoCambioUSD
        }
        if (precioKgUSD) {
          setFieldValue("precioKg", precioKgUSD)
          if (pesoVenta) {
            setFieldValue("precioCabeza", precioKgUSD * pesoVenta)
          }
        }
        setFieldValue("moneda", "USD")
      })
      .catch(err => {
        const status = err?.response?.status
        if (status === 403) {
          setError(
            "Sin permiso para consultar precios de referencia. Ingresá los valores manualmente.",
          )
        } else if (status === 404 && valorRef?.precioKilo != null) {
          // Fallback: usar precio de referencia del banco (valor_ganaderia)
          const precioKgUSD = Number(valorRef.precioKilo)
          setFieldValue("precioKg", precioKgUSD)
          if (pesoVenta) setFieldValue("precioCabeza", precioKgUSD * pesoVenta)
          setFieldValue("moneda", "USD")
        } else if (status === 404) {
          setError(
            `No hay precio registrado para la categoría ${cat.descripcion}.`,
          )
        } else {
          setError(
            "Error al consultar precios de referencia. Ingresá los valores manualmente.",
          )
        }
      })
  }, [values.idCategoria, categorias, valoresGanaderia, tipoCambioUSD])

  // mapa: codigoEspecie → { codigoCategoria (o '') → cantidadDisponible }
  const stockMap = useMemo(() => {
    const map = {}
    stockDisponible.forEach(item => {
      if (!map[item.codigoEspecie]) map[item.codigoEspecie] = {}
      map[item.codigoEspecie][item.codigoCategoria || ""] =
        item.cantidadDisponible
    })
    return map
  }, [stockDisponible])

  // Filtrar especies: solo las que tienen stock en modo EGRESO
  const especiesOpciones = useMemo(() => {
    if (values.tipoMovimiento === "EGRESO") {
      const codigosEnStock = new Set(stockDisponible.map(s => s.codigoEspecie))
      return especies.filter(e => codigosEnStock.has(e.codigo))
    }
    return especies
  }, [especies, stockDisponible, values.tipoMovimiento])

  // Filtrar categorías: solo las que tienen stock para la especie seleccionada en modo EGRESO
  const categoriasOpciones = useMemo(() => {
    if (values.tipoMovimiento === "EGRESO" && values.idEspecie) {
      const esp = especies.find(e => e.id === parseInt(values.idEspecie))
      if (esp && stockMap[esp.codigo]) {
        const codigosConStock = Object.keys(stockMap[esp.codigo])
        return categorias.filter(c => codigosConStock.includes(c.codigo || ""))
      }
      return []
    }
    return categorias
  }, [categorias, stockMap, especies, values.idEspecie, values.tipoMovimiento])

  // Pre-completar cantidad y actualizar stockMax al seleccionar especie+categoría en EGRESO
  useEffect(() => {
    if (values.tipoMovimiento !== "EGRESO" || !values.idEspecie) {
      stockMaxRef.current = null
      setStockMax(null)
      return
    }
    const esp = especies.find(e => e.id === parseInt(values.idEspecie))
    if (!esp || !stockMap[esp.codigo]) {
      stockMaxRef.current = null
      setStockMax(null)
      return
    }
    const catCodigo = values.idCategoria
      ? (categorias.find(c => c.id === parseInt(values.idCategoria))?.codigo ??
        "")
      : ""
    const disponible = stockMap[esp.codigo][catCodigo]
    if (disponible != null) {
      stockMaxRef.current = disponible
      setStockMax(disponible)
      setFieldValue("cantidadCabezas", disponible)
    } else {
      stockMaxRef.current = null
      setStockMax(null)
    }
  }, [
    values.idEspecie,
    values.idCategoria,
    values.tipoMovimiento,
    stockMap,
    categorias,
    especies,
  ])

  // Cambiar tipo de movimiento: resetea motivo, especie, categoría y stockMax
  const handleTipoChange = e => {
    const nuevoTipo = e.target.value
    setFieldValue("tipoMovimiento", nuevoTipo)
    setFieldValue("idEspecie", "")
    setFieldValue("idCategoria", "")
    setFieldValue("cantidadCabezas", "")
    stockMaxRef.current = null
    setStockMax(null)
    const nuevosMotivos = MOTIVOS[nuevoTipo]
    if (nuevosMotivos && nuevosMotivos.length > 0) {
      setFieldValue("motivo", nuevosMotivos[0].value)
    }
  }

  // Calcular importe total automáticamente
  useEffect(() => {
    if (MOTIVOS_ECONOMICOS.includes(values.motivo)) {
      const cantidad = parseFloat(values.cantidadCabezas) || 0
      const peso = parseFloat(values.pesoTotalKg) || 0
      const precioKg = parseFloat(values.precioKg) || 0
      const precioCabeza = parseFloat(values.precioCabeza) || 0

      let importe = 0
      if (peso > 0 && precioKg > 0) {
        importe = peso * precioKg
      } else if (cantidad > 0 && precioCabeza > 0) {
        importe = cantidad * precioCabeza
      }

      if (importe > 0) {
        setFieldValue("importeTotal", importe)
      }
    }
  }, [
    values.cantidadCabezas,
    values.pesoTotalKg,
    values.precioKg,
    values.precioCabeza,
    values.motivo,
  ])

  // Calcular peso total al cambiar cantidad de cabezas si hay peso promedio inferido de la tabla
  useEffect(() => {
    if (!promedioKgsCabeza || !values.cantidadCabezas) return
    const cantidad = parseFloat(values.cantidadCabezas) || 0
    if (cantidad > 0) {
      setFieldValue("pesoTotalKg", cantidad * promedioKgsCabeza)
    }
  }, [values.cantidadCabezas, promedioKgsCabeza])

  // Convertir precios al cambiar moneda (ARS↔USD)
  const prevMonedaRef = useRef(null)
  useEffect(() => {
    const prev = prevMonedaRef.current
    prevMonedaRef.current = values.moneda
    if (prev === null || prev === values.moneda || !tipoCambioUSD) return

    const factor = values.moneda === "USD" ? 1 / tipoCambioUSD : tipoCambioUSD
    if (values.precioKg !== "")
      setFieldValue("precioKg", Number(values.precioKg) * factor)
    if (values.precioCabeza !== "")
      setFieldValue("precioCabeza", Number(values.precioCabeza) * factor)
    // importeTotal se recalcula solo por el useEffect de importe
  }, [values.moneda])

  const mostrarDatosEconomicos = MOTIVOS_ECONOMICOS.includes(values.motivo)
  const mostrarDatosTraslado = MOTIVOS_TRASLADO.includes(values.motivo)

  return (
    <Card>
      <CardBody>
        <h5 className="card-title mb-4">
          <i className="mdi mdi-swap-vertical me-2" />
          Registrar Movimiento Ganadero
        </h5>

        <Form onSubmit={handleSubmit}>
          {/* Tipo de Movimiento */}
          <Row className="mb-4">
            <Col md={12}>
              <Label className="d-block mb-2">Tipo de Movimiento *</Label>
              <div className="d-flex gap-3">
                {TIPOS_MOVIMIENTO.map(tipo => (
                  <div
                    key={tipo.value}
                    className="form-check form-check-inline"
                  >
                    <Input
                      type="radio"
                      id={`tipo-${tipo.value}`}
                      name="tipoMovimiento"
                      value={tipo.value}
                      checked={values.tipoMovimiento === tipo.value}
                      onChange={handleTipoChange}
                      className="form-check-input"
                    />
                    <Label
                      className="form-check-label"
                      htmlFor={`tipo-${tipo.value}`}
                    >
                      <i
                        className={`mdi mdi-${tipo.icon}-bold text-${tipo.color} me-1`}
                      />
                      {tipo.label}
                    </Label>
                  </div>
                ))}
              </div>
            </Col>
          </Row>

          <Row>
            {/* Establecimiento */}
            <Col md={6}>
              <FormGroup>
                <Label>Establecimiento *</Label>
                <Input
                  type="select"
                  name="idEstablecimiento"
                  value={values.idEstablecimiento}
                  onChange={handleChange}
                  invalid={
                    touched.idEstablecimiento && !!errors.idEstablecimiento
                  }
                >
                  <option value="">Seleccione...</option>
                  {establecimientos.map(est => (
                    <option key={est.id} value={est.id}>
                      {/* BDSJEA-134 */}
                      {est.nombre}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{errors.idEstablecimiento}</FormFeedback>
              </FormGroup>
            </Col>

            {/* Fecha */}
            <Col md={3}>
              <FormGroup>
                <Label>Fecha de Movimiento *</Label>
                <Flatpickr
                  className="form-control"
                  value={values.fechaMovimiento}
                  onChange={([date]) => setFieldValue("fechaMovimiento", date)}
                  options={{
                    dateFormat: "d/m/Y",
                    locale: Spanish,
                    maxDate: "today",
                  }}
                />
                {touched.fechaMovimiento && errors.fechaMovimiento && (
                  <div className="text-danger small mt-1">
                    {errors.fechaMovimiento}
                  </div>
                )}
              </FormGroup>
            </Col>

            {/* Número DTE */}
            <Col md={3}>
              <FormGroup>
                <Label>Nro. DTE/Guía</Label>
                <Input
                  type="text"
                  name="numeroDte"
                  value={values.numeroDte}
                  onChange={handleChange}
                  placeholder="Opcional"
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            {/* Motivo */}
            <Col md={4}>
              <FormGroup>
                <Label>Motivo *</Label>
                <Input
                  type="select"
                  name="motivo"
                  value={values.motivo}
                  onChange={handleChange}
                  invalid={touched.motivo && !!errors.motivo}
                >
                  {MOTIVOS[values.tipoMovimiento].map(motivo => (
                    <option key={motivo.value} value={motivo.value}>
                      {motivo.label}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{errors.motivo}</FormFeedback>
              </FormGroup>
            </Col>

            {/* Especie */}
            <Col md={4}>
              <FormGroup>
                <Label>Especie *</Label>
                <Input
                  type="select"
                  name="idEspecie"
                  value={values.idEspecie}
                  onChange={handleChange}
                  invalid={touched.idEspecie && !!errors.idEspecie}
                >
                  <option value="">Seleccione...</option>
                  {especiesOpciones.map(esp => (
                    <option key={esp.id} value={esp.id}>
                      {esp.descripcion}
                    </option>
                  ))}
                </Input>
                {values.tipoMovimiento === "EGRESO" &&
                  especiesOpciones.length === 0 && (
                    <small className="text-warning">
                      No hay especies con stock disponible en este
                      establecimiento
                    </small>
                  )}
                <FormFeedback>{errors.idEspecie}</FormFeedback>
              </FormGroup>
            </Col>

            {/* Categoría */}
            <Col md={4}>
              <FormGroup>
                <Label>Categoría</Label>
                <Input
                  type="select"
                  name="idCategoria"
                  value={values.idCategoria}
                  onChange={handleChange}
                  disabled={!values.idEspecie}
                >
                  <option value="">Seleccione...</option>
                  {categoriasOpciones.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.descripcion}
                    </option>
                  ))}
                </Input>
                {!values.idEspecie && (
                  <small className="text-muted">
                    Seleccione una especie para habilitar las categorías
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>

          {/* Cantidad de Cabezas */}
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Cantidad de Cabezas *</Label>
                <NumberInput
                  nroDecimales={0}
                  min={1}
                  value={
                    values.cantidadCabezas !== ""
                      ? Number(values.cantidadCabezas)
                      : ""
                  }
                  onChange={val => setFieldValue("cantidadCabezas", val)}
                  placeholder="Ej: 50"
                  className={
                    touched.cantidadCabezas && errors.cantidadCabezas
                      ? "is-invalid"
                      : ""
                  }
                />
                {touched.cantidadCabezas && errors.cantidadCabezas && (
                  <div className="invalid-feedback d-block">
                    {errors.cantidadCabezas}
                  </div>
                )}
                {values.tipoMovimiento === "EGRESO" && stockMax != null && (
                  <small className="text-muted">
                    Stock disponible: {stockMax} cabezas
                  </small>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Cabezas para la Venta</Label>
                <Input
                  type="number"
                  name="cantidadCabezasVenta"
                  value={values.cantidadCabezasVenta}
                  onChange={handleChange}
                  placeholder={
                    values.cantidadCabezas
                      ? `Por defecto: ${values.cantidadCabezas}`
                      : "Por defecto: igual a total"
                  }
                />
                <small className="text-muted">
                  Si se deja vacío, se asume igual a la cantidad total
                </small>
              </FormGroup>
            </Col>
          </Row>

          {/* Datos Económicos (solo para COMPRA/VENTA) */}
          {mostrarDatosEconomicos && (
            <>
              <hr className="my-4" />
              <h6 className="mb-3">
                <i className="mdi mdi-cash me-2" />
                Datos Económicos
              </h6>
              <Alert color="info" className="py-2">
                <small>
                  <i className="mdi mdi-information me-1" />
                  Los campos se pre-completan desde la tabla de precios de
                  referencia según la categoría seleccionada (moneda: USD).
                  Podés modificarlos manualmente. El importe total se calculará
                  automáticamente.
                </small>
              </Alert>

              <Row>
                <Col md={2}>
                  <FormGroup>
                    <Label>Precios expresados en</Label>
                    <Input
                      type="select"
                      name="moneda"
                      value={values.moneda}
                      onChange={handleChange}
                    >
                      <option value="ARS">ARS</option>
                      <option value="USD">USD</option>
                    </Input>
                    {values.moneda === "ARS" && tipoCambioUSD && (
                      <small style={{ color: "#000" }}>
                        1 USD ={" "}
                        {tipoCambioUSD.toLocaleString("es-AR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        ARS
                      </small>
                    )}
                  </FormGroup>
                </Col>

                <Col md={3}>
                  <FormGroup>
                    <Label>Precio por Kg</Label>
                    <NumberInput
                      nroDecimales={2}
                      value={
                        values.precioKg !== "" ? Number(values.precioKg) : ""
                      }
                      onChange={val => setFieldValue("precioKg", val)}
                    />
                  </FormGroup>
                </Col>

                <Col md={3}>
                  <FormGroup>
                    <Label>Precio por Cabeza</Label>
                    <NumberInput
                      nroDecimales={2}
                      value={
                        values.precioCabeza !== ""
                          ? Number(values.precioCabeza)
                          : ""
                      }
                      onChange={val => setFieldValue("precioCabeza", val)}
                    />
                    {promedioKgsCabeza != null && (
                      <small className="text-muted">
                        Peso prom.: {promedioKgsCabeza} kg/cab.
                      </small>
                    )}
                  </FormGroup>
                </Col>

                <Col md={2}>
                  <FormGroup>
                    <Label>Peso Total (kg)</Label>
                    <NumberInput
                      nroDecimales={2}
                      min={0}
                      value={
                        values.pesoTotalKg !== ""
                          ? Number(values.pesoTotalKg)
                          : ""
                      }
                      onChange={val => setFieldValue("pesoTotalKg", val)}
                    />
                  </FormGroup>
                </Col>

                <Col md={2}>
                  <FormGroup>
                    <Label>Importe Total</Label>
                    <NumberInput
                      nroDecimales={2}
                      value={
                        values.importeTotal !== ""
                          ? Number(values.importeTotal)
                          : ""
                      }
                      onChange={val => setFieldValue("importeTotal", val)}
                      placeholder="Auto"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </>
          )}

          {/* Datos de Traslado */}
          {mostrarDatosTraslado && (
            <>
              <hr className="my-4" />
              <h6 className="mb-3">
                <i className="mdi mdi-truck me-2" />
                Datos de Traslado
              </h6>

              <Row>
                {values.tipoMovimiento === "INGRESO" && (
                  <Col md={6}>
                    <FormGroup>
                      <Label>RENSPA Origen *</Label>
                      <Input
                        type="text"
                        name="renspaOrigen"
                        value={values.renspaOrigen}
                        onChange={handleChange}
                        invalid={touched.renspaOrigen && !!errors.renspaOrigen}
                        placeholder="Ej: 15.123.1.12345/A0"
                      />
                      <FormFeedback>{errors.renspaOrigen}</FormFeedback>
                    </FormGroup>
                  </Col>
                )}

                {values.tipoMovimiento === "EGRESO" && (
                  <Col md={6}>
                    <FormGroup>
                      <Label>RENSPA Destino *</Label>
                      <Input
                        type="text"
                        name="renspaDestino"
                        value={values.renspaDestino}
                        onChange={handleChange}
                        invalid={
                          touched.renspaDestino && !!errors.renspaDestino
                        }
                        placeholder="Ej: 15.123.1.12345/A0"
                      />
                      <FormFeedback>{errors.renspaDestino}</FormFeedback>
                    </FormGroup>
                  </Col>
                )}

                <Col md={6}>
                  <FormGroup>
                    <Label>Establecimiento Contraparte *</Label>
                    <Input
                      type="text"
                      name="establecimientoContraparte"
                      value={values.establecimientoContraparte}
                      onChange={handleChange}
                      invalid={
                        touched.establecimientoContraparte &&
                        !!errors.establecimientoContraparte
                      }
                      placeholder="Nombre del establecimiento"
                    />
                    <FormFeedback>
                      {errors.establecimientoContraparte}
                    </FormFeedback>
                  </FormGroup>
                </Col>
              </Row>
            </>
          )}

          {/* Botones */}
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button color="secondary" onClick={onCancel} disabled={loading}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <i className="mdi mdi-content-save me-1" />
                  Registrar Movimiento
                </>
              )}
            </Button>
          </div>
        </Form>
      </CardBody>
    </Card>
  )
}

export default FormMovimientoGanadero
